// App-side bridge client. A third-party host app calls connectLeestoApp() once so
// the Livery shell can talk to it. Vanilla (no React) — works in any app, and is
// a no-op when the page isn't embedded in a shell (window.parent === window).

import brand from "@leesto/ui/theme/brand";
import {
  type AppBuild,
  type AppToShell,
  isLeestoMessage,
  LEESTO_BRIDGE_VERSION,
  type ShellToApp,
} from "./bridge";
import {
  createDiagnosticRunId,
  diagnosticRunIdFromSearch,
  hasDiagnosticsParam,
} from "./diagnostics";
import { type DiagnosticsHandle, installDiagnostics } from "./diagnostics-capture";
import { type RrwebRecorderHandle, startRrwebRecorder } from "./rrweb-recorder";
import { isAllowedShellOrigin, resolveShellOrigins } from "./shell-origins";

export type ConnectLeestoAppOptions = {
  /** Color of the highlight outline drawn on `leesto:highlight`. */
  highlightColor?: string;
  /** Returns a screenshot of the app as a data URL. When omitted, the app
   *  screenshots its own `document.body` with modern-screenshot (lazy-loaded on
   *  first capture) — so the Livery shell gets a prompt-free, chrome-free shot
   *  with zero extra code. Provide this to override (e.g. capture a subtree). */
  capture?: () => Promise<string>;
  /** Build descriptor of this app (version + environment) to stamp on reports.
   *  Best-effort: falls back to window.__LEESTO_APP_BUILD__ then <meta> tags. */
  build?: AppBuild;
  /** Capture des requêtes réseau récentes (métadonnées, ring buffer) comme
   *  diagnostic — joint à la session enregistrée. Défaut : activé. */
  captureNetwork?: boolean;
  /** Origines supplémentaires auxquelles transmettre l'identifiant de run.
   *  L'origine de l'app est toujours autorisée ; vide = aucun cross-origin. */
  runIdOrigins?: string[];
  /** Origines de shell autorisées à piloter ce connecteur. Déclarer cette
   *  option REMPLACE les origines Leesto par défaut (shell auto-hébergé). */
  shellOrigins?: string[];
};

/** Vrai si l'utilisateur a activé Global Privacy Control (navigator.globalPrivacyControl).
 *  Signal d'opt-out légalement opposable (CCPA & co.) : quand il est présent, la
 *  capture de session ne doit pas avoir lieu. Best-effort / SSR-safe. */
export function gpcHonored(): boolean {
  return (
    typeof navigator !== "undefined" &&
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  );
}

/** Resolve the app build descriptor, best-effort. Order: explicit option →
 *  window.__LEESTO_APP_BUILD__ → <meta name="app-version|app-environment">.
 *  Empty/whitespace values are dropped; returns undefined when nothing usable. */
export function resolveBuild(explicit: AppBuild | undefined): AppBuild | undefined {
  const clean = (v: unknown): string | undefined => {
    const s = typeof v === "string" ? v.trim() : "";
    return s.length > 0 ? s : undefined;
  };
  const metaContent = (name: string): string | undefined => {
    if (typeof document === "undefined") return undefined;
    return clean(document.querySelector(`meta[name="${name}"]`)?.getAttribute("content"));
  };
  const globalBuild =
    typeof window !== "undefined"
      ? (window as unknown as { __LEESTO_APP_BUILD__?: AppBuild }).__LEESTO_APP_BUILD__
      : undefined;

  const version =
    clean(explicit?.version) ?? clean(globalBuild?.version) ?? metaContent("app-version");
  const environment =
    clean(explicit?.environment) ??
    clean(globalBuild?.environment) ??
    metaContent("app-environment");

  if (!version && !environment) return undefined;
  return { ...(version ? { version } : {}), ...(environment ? { environment } : {}) };
}

/** Resolve the app's declared favicon while still inside its own document. */
export function resolveFavicon(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const link = document.querySelector<HTMLLinkElement>(
    'link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]',
  );
  if (!link?.href) return undefined;
  try {
    return new URL(link.href, location.href).href;
  } catch {
    return undefined;
  }
}

/** Effective page background: dark apps usually paint it on <html> (or a root
 *  container), leaving <body> transparent. Forcing white here would bleed
 *  through wherever body doesn't paint — the "white band" around the capture.
 *  Resolve body → html → white so the shot matches what the user sees. */
function pageBackgroundColor(): string {
  const isOpaque = (c: string) =>
    !!c && c !== "transparent" && !/^rgba\(\s*0,\s*0,\s*0,\s*0\s*\)$/.test(c);
  const bodyBg = getComputedStyle(document.body).backgroundColor;
  if (isOpaque(bodyBg)) return bodyBg;
  const htmlBg = getComputedStyle(document.documentElement).backgroundColor;
  if (isOpaque(htmlBg)) return htmlBg;
  return "#ffffff";
}

/** Default self-screenshot: render the app's own DOM to a PNG data URL — the
 *  VISIBLE VIEWPORT at the current scroll, i.e. what the tester actually sees.
 *
 *  We capture <html> cropped to the viewport box and translate it up by the
 *  scroll offset (`style.transform`). modern-screenshot clones the DOM into an
 *  SVG foreignObject where `position: fixed`/`sticky` no longer resolves against
 *  the viewport — and our root translate would drag those elements off-screen —
 *  so we re-pin them with an opposite translate for the duration of the capture.
 *  (Capturing `document.body` full-height instead left fixed sidebars/headers
 *  collapsed and the main content visually offset.)
 *
 *  The app runs in its own document, so this captures ONLY the app — no Livery
 *  chrome (that lives in the parent shell). modern-screenshot is dynamically
 *  imported so it's only pulled into the bundle on first capture. */
async function defaultCapture(): Promise<string> {
  const { domToPng } = await import("modern-screenshot");
  const root = document.documentElement;
  const sx = window.scrollX;
  const sy = window.scrollY;

  // Re-pin fixed/sticky elements so they don't ride the root translate.
  const pinned: Array<[HTMLElement, string]> = [];
  for (const el of root.querySelectorAll<HTMLElement>("*")) {
    const pos = getComputedStyle(el).position;
    if (pos === "fixed" || pos === "sticky") {
      const prev = el.style.transform;
      pinned.push([el, prev]);
      el.style.transform = `translate(${sx}px, ${sy}px)${prev ? ` ${prev}` : ""}`;
    }
  }

  try {
    return await domToPng(root, {
      quality: 0.92,
      backgroundColor: pageBackgroundColor(),
      width: root.clientWidth,
      height: root.clientHeight,
      style: { transform: `translate(${-sx}px, ${-sy}px)`, transformOrigin: "top left" },
    });
  } finally {
    for (const [el, prev] of pinned) el.style.transform = prev;
  }
}

/** Best-effort, reasonably-stable CSS selector for an element. */
function buildSelector(el: Element): string {
  if (el.id) return `#${CSS.escape(el.id)}`;
  const parts: string[] = [];
  let node: Element | null = el;
  while (node && node.nodeType === 1 && node !== document.body && parts.length < 4) {
    if (node.id) {
      parts.unshift(`#${CSS.escape(node.id)}`);
      break;
    }
    let part = node.tagName.toLowerCase();
    const cls =
      typeof node.className === "string"
        ? node.className.trim().split(/\s+/).filter(Boolean)[0]
        : null;
    if (cls) part += `.${CSS.escape(cls)}`;
    const parent: Element | null = node.parentElement;
    if (parent) {
      const sameTag = Array.from(parent.children).filter((c) => c.tagName === node?.tagName);
      if (sameTag.length > 1) part += `:nth-of-type(${sameTag.indexOf(node) + 1})`;
    }
    parts.unshift(part);
    node = parent;
  }
  return parts.join(" > ");
}

/** Short label: tag + a trimmed text snippet. */
function labelOf(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const text = (el.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, 40);
  return text ? `${tag} · ${text}` : tag;
}

export function connectLeestoApp(options: ConnectLeestoAppOptions = {}): () => void {
  if (typeof window === "undefined" || window.parent === window) return () => {};
  const parent = window.parent;
  const color = options.highlightColor ?? brand.light.primary;

  // Allowlist des origines de shell (cf. shell-origins.ts). Le connecteur tourne
  // dans l'app d'un TIERS : il ne se laisse piloter que par un shell connu, et
  // n'adresse ses messages qu'à une origine explicite — jamais de wildcard.
  const allowedOrigins = resolveShellOrigins({
    explicit: options.shellOrigins,
    pageOrigin: location.origin,
  });

  // Le referrer n'est qu'un INDICE pour épingler l'origine tout de suite, jamais
  // une preuve : après une navigation interne il vaut l'origine de l'app
  // elle-même, donc une valeur hors allowlist ne prouve rien. C'est le premier
  // message entrant valide qui tranche — `event.origin` est posé par le
  // navigateur, lui n'est pas falsifiable.
  let shellOrigin: string | null = null;
  try {
    const fromReferrer = document.referrer ? new URL(document.referrer).origin : "";
    if (isAllowedShellOrigin(fromReferrer, allowedOrigins)) shellOrigin = fromReferrer;
  } catch {
    /* pas d'indice exploitable */
  }

  // Avant l'épinglage, le message est adressé à CHAQUE origine allowlistée :
  // postMessage ne délivre qu'à celle qui correspond réellement au parent, donc
  // rien ne fuit vers les autres.
  const post = (msg: AppToShell) => {
    if (shellOrigin) {
      parent.postMessage(msg, shellOrigin);
      return;
    }
    for (const origin of allowedOrigins) parent.postMessage(msg, origin);
  };

  let highlighted: HTMLElement | null = null;
  let prevOutline = "";
  const clearHighlight = () => {
    if (highlighted) highlighted.style.outline = prevOutline;
    highlighted = null;
  };

  // --- Element picker -------------------------------------------------------
  let stopPick: (() => void) | null = null;
  const startPick = () => {
    if (stopPick) return;
    let hovered: HTMLElement | null = null;
    let hoverPrev = "";
    const setHover = (el: HTMLElement | null) => {
      if (hovered === el) return;
      if (hovered) hovered.style.outline = hoverPrev;
      hovered = el;
      if (el) {
        hoverPrev = el.style.outline;
        el.style.outline = `2px dashed ${color}`;
        el.style.outlineOffset = "1px";
      }
    };
    const onMove = (e: MouseEvent) => setHover(e.target instanceof HTMLElement ? e.target : null);
    const onClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = e.target;
      const target = el instanceof HTMLElement ? el : null;
      stopPick?.();
      if (target) {
        const r = target.getBoundingClientRect();
        post({
          type: "leesto:element-picked",
          selector: buildSelector(target),
          label: labelOf(target),
          rect: { x: r.x, y: r.y, width: r.width, height: r.height },
        });
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        stopPick?.();
        post({ type: "leesto:pick-cancelled" });
      }
    };
    stopPick = () => {
      if (hovered) hovered.style.outline = hoverPrev;
      hovered = null;
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKey, true);
      document.body.style.cursor = "";
      stopPick = null;
    };
    document.body.style.cursor = "crosshair";
    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKey, true);
  };

  // --- Diagnostic technique (console + réseau) ------------------------------
  // Armé par l'URL et lu SYNCHRONEMENT ici. Un armement par message bridge
  // laisserait passer tout le démarrage de l'app — échec de mount, appels API
  // initiaux en erreur, écran blanc — soit la classe de bug la plus coûteuse en
  // test. Sans le marqueur, RIEN n'est patché : le SDK reste inerte hors d'une
  // session Lab consentie. Double garde GPC (le shell n'arme déjà pas).
  const diagnosticsArmed = hasDiagnosticsParam(location.search) && !gpcHonored();
  const diagnosticRunId = diagnosticsArmed
    ? (diagnosticRunIdFromSearch(location.search) ?? createDiagnosticRunId())
    : undefined;
  const diagnostics: DiagnosticsHandle | null = diagnosticsArmed
    ? installDiagnostics({
        captureNetwork: options.captureNetwork !== false,
        runId: diagnosticRunId,
        runIdOrigins: options.runIdOrigins,
      })
    : null;

  let recorder: RrwebRecorderHandle | null = null;
  let recorderStarting = false;
  let stopRequested = false;
  const stopRecording = () => {
    if (recorder) {
      const durationMs = recorder.stop();
      recorder = null;
      post({ type: "leesto:rec-stopped", durationMs });
    } else if (recorderStarting) {
      // rec-stop arrivé pendant l'init async : finaliser dès que le handle résout.
      stopRequested = true;
    }
  };

  // Annonce l'état complet du connecteur (URL courante, favicon, build, GPC…).
  // Appelée à la connexion ET sur chaque `leesto:ping` : le shell (Lab) rend
  // l'iframe en SSR, donc cette page peut avoir chargé et annoncé son `ready`
  // AVANT que le shell hydrate et écoute — l'annonce partait alors dans le vide
  // et le shell restait « connexion en cours » jusqu'à un refresh. Le ping,
  // émis par le shell dès qu'il écoute, fait rejouer l'annonce.
  const build = resolveBuild(options.build);
  let lastReported = location.href;
  const announceReady = () => {
    lastReported = location.href;
    const favicon = resolveFavicon();
    post({
      type: "leesto:ready",
      url: location.href,
      // Annonce la version du protocole : un connecteur épinglé sur npm se fige
      // pendant que le shell évolue, et un désaccord échoue en SILENCE sans ça.
      bridgeVersion: LEESTO_BRIDGE_VERSION,
      ...(favicon ? { favicon } : {}),
      ...(build ? { build } : {}),
      ...(diagnosticRunId ? { diagnosticRunId } : {}),
      // `gpc` prévient le shell dès la connexion que l'enregistrement est bloqué
      // (conformité Global Privacy Control), pour qu'il n'offre pas la capture.
      ...(gpcHonored() ? { gpc: true } : {}),
    });
  };

  const onMessage = (event: MessageEvent) => {
    if (event.source !== parent) return;
    if (!isAllowedShellOrigin(event.origin, allowedOrigins)) return;
    if (!isLeestoMessage(event.data)) return;
    // Première origine valide → épinglée : les messages suivants ne partent
    // plus qu'à elle.
    if (!shellOrigin) shellOrigin = event.origin;
    const msg = event.data as ShellToApp;
    if (msg.type === "leesto:ping") {
      post({ type: "leesto:pong" });
      announceReady();
    } else if (msg.type === "leesto:highlight") {
      clearHighlight();
      if (msg.selector) {
        // Le sélecteur vient du pont : `querySelector` lève une SyntaxError s'il
        // est malformé, et elle s'échapperait de ce listener en erreur NON
        // capturée dans l'app du tiers. Un surlignage raté n'est pas une panne.
        let el: HTMLElement | null = null;
        try {
          el = document.querySelector<HTMLElement>(msg.selector);
        } catch {
          el = null;
        }
        if (el) {
          highlighted = el;
          prevOutline = el.style.outline;
          el.style.outline = `2px solid ${color}`;
          el.style.outlineOffset = "2px";
          el.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      }
    } else if (msg.type === "leesto:pick-start") {
      startPick();
    } else if (msg.type === "leesto:pick-stop") {
      stopPick?.();
    } else if (msg.type === "leesto:capture-request") {
      const capture = options.capture ?? defaultCapture;
      capture()
        .then((dataUrl) => post({ type: "leesto:screenshot", dataUrl }))
        .catch((err) =>
          post({
            type: "leesto:capture-failed",
            reason: err instanceof Error ? err.message : "capture_error",
          }),
        );
    } else if (msg.type === "leesto:rec-start") {
      if (recorder || recorderStarting) return; // déjà en cours / en démarrage
      // Gate conformité : si Global Privacy Control est actif, la capture de
      // session est légalement interdite — on la refuse AVANT tout démarrage,
      // sans jamais instancier le recorder (aucune donnée n'est captée). ECH-290.
      if (gpcHonored()) {
        post({ type: "leesto:rec-blocked", reason: "gpc" });
        return;
      }
      recorderStarting = true;
      stopRequested = false;
      startRrwebRecorder({
        onBatch: (events) => post({ type: "leesto:rec-batch", events }),
        onCap: () => stopRecording(),
        captureNetwork: options.captureNetwork,
        // Armé → le recorder se branche sur la capture permanente (les erreurs
        // restent entrelacées dans le replay) sans la détruire à l'arrêt.
        // Sinon il en crée une éphémère, comme avant.
        diagnostics,
      })
        .then((h) => {
          recorderStarting = false;
          recorder = h;
          if (stopRequested) stopRecording(); // stop demandé pendant l'init
        })
        .catch((err) => {
          recorderStarting = false;
          post({
            type: "leesto:rec-error",
            reason: err instanceof Error ? err.message : "rec_error",
          });
        });
    } else if (msg.type === "leesto:rec-stop") {
      stopRecording();
    } else if (msg.type === "leesto:diagnostics-request") {
      // Toujours répondre, même sans capture armée : sinon le shell attend son
      // timeout pour rien à chaque signalement.
      post({
        type: "leesto:diagnostics",
        snapshot: diagnostics?.snapshot() ?? { errors: [], logs: [], network: [] },
      });
    }
  };

  window.addEventListener("message", onMessage);

  // Announce readiness + current location, and report SPA navigations.
  announceReady();
  // Les navigations d'un routeur client (React Router, TanStack, Next App Router)
  // passent par history.pushState/replaceState, qui NE déclenchent PAS `popstate`.
  // Sans ce patch le shell restait sur l'URL d'entrée : le testeur signalait un
  // bug depuis /facturation et le ticket portait /dashboard. On patche donc ces
  // deux méthodes — uniquement dans une page EMBARQUÉE (connectLeestoApp a déjà
  // rendu la main autrement), et on les restaure à la déconnexion.
  const reportLocation = () => {
    if (location.href === lastReported) return; // pas de doublon sur le pont
    lastReported = location.href;
    post({ type: "leesto:location", url: location.href });
  };

  const nativePushState = history.pushState;
  const nativeReplaceState = history.replaceState;
  const patchedPushState: History["pushState"] = (...args) => {
    nativePushState.apply(history, args);
    reportLocation();
  };
  const patchedReplaceState: History["replaceState"] = (...args) => {
    nativeReplaceState.apply(history, args);
    reportLocation();
  };
  history.pushState = patchedPushState;
  history.replaceState = patchedReplaceState;

  window.addEventListener("popstate", reportLocation);
  window.addEventListener("hashchange", reportLocation);

  return () => {
    stopRecording();
    diagnostics?.teardown();
    clearHighlight();
    stopPick?.();
    window.removeEventListener("message", onMessage);
    window.removeEventListener("popstate", reportLocation);
    window.removeEventListener("hashchange", reportLocation);
    // Ne restaure que SI personne n'a patché par-dessus nous entre-temps :
    // écraser le patch d'une autre lib serait pire que de laisser le nôtre.
    if (history.pushState === patchedPushState) history.pushState = nativePushState;
    if (history.replaceState === patchedReplaceState) history.replaceState = nativeReplaceState;
  };
}
